import { useMarket } from "@app/_context";
import { getWalleyCheckoutForm } from "@server/requests/checkout";
import { getStockWatch } from "@server/requests/checkout/stockWatch";
import getElevateSession from "@server/requests/elevate/getElevateSession";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCookies } from "cookies-next";
import { type RefObject, useCallback, useEffect, useRef } from "react";

import { BRAND_CODE } from "@/config/common";
import { getCartCookie } from "@/utils/cart/getCartCookie";

import useEvents from "@app/_hooks/useEvents";
import { useCheckout } from "../../Checkout.hooks";
import { checkoutStore } from "../../Checkout.store";
import { WalleyPaymentType } from "../../Checkout.types";
import { cartToStockWatch } from "../../Checkout.utils";
import { getWalleyReturnUrl, getWalleyTermsUrl } from "./Walley.utils";

/**
 *
 * Walley Checkout form
 * @returns Walley Checkout form snippet
 */
export function useWalleyCheckoutForm() {
  const { market } = useMarket().state;
  const newSession = useRef(false);
  const paymentType = checkoutStore((state) => state.paymentType);
  const { data, refetch } = useCheckout();
  const elevateSession = getElevateSession();
  const cookies = Object.fromEntries(
    Object.entries(getCookies() ?? {}).filter(
      (entry): entry is [string, string] => entry[1] !== undefined,
    ),
  );

  useEffect(() => {
    newSession.current = true;
  }, [paymentType]);
  return useQuery({
    queryKey: ["walleyCheckoutForm", paymentType],
    queryFn: () =>
      getStockWatch(cartToStockWatch(data)).then(async (stockWatch) => {
        if (stockWatch.length > 0) {
          newSession.current = false;
          await refetch();
          window.scrollTo({ top: 0, behavior: "smooth" });
          /**
           * @todo handle stock watch error more gracefully with actual error message
           */
          throw "";
        }

        return getWalleyCheckoutForm({
          cartId: getCartCookie()?.value ?? "",
          marketCode: market.code,
          brandCode: BRAND_CODE,
          type: "desktop",
          shopCallbackUrl: getWalleyReturnUrl(market.locale),
          shopTermsUrl: getWalleyTermsUrl(market.locale),
          newSession: newSession.current,
          storeCode:
            paymentType === WalleyPaymentType.b2b
              ? market.walleyB2BStoreId
              : market.walleyB2CStoreId,
          elevateSessionKey: elevateSession.sessionKey,
          elevateCustomerKey: elevateSession.customerKey,
          elevateMarket: market.elevateMarket,
          cookies,
        }).then((response) => {
          newSession.current = false;
          return response;
        });
      }),
  });
}

/**
 * Render Walley Checkout widget
 */
export function useWalleyCheckoutMount({
  ref,
  snippet,
}: {
  ref?: RefObject<HTMLDivElement | null>;
  snippet?: string;
}) {
  useEffect(() => {
    renderWalleyCheckoutSnippet({ ref, snippet });
  }, [ref, snippet]);
}

/**
 * Render the Walley payment form HTML snippet
 */
export function renderWalleyCheckoutSnippet({
  ref,
  snippet,
}: {
  ref?: RefObject<HTMLDivElement | null>;
  snippet?: string;
}) {
  if (!ref?.current) {
    console.error("renderWalleyCheckoutSnippet ref is not defined");
    return;
  }

  if (!snippet) {
    console.error("renderWalleyCheckoutSnippet snippet is not defined");
    return;
  }

  ref.current.innerHTML = snippet;

  const script = document.createElement("script");
  const scriptElementFromRef = ref.current.getElementsByTagName("script")[0];

  if (scriptElementFromRef) {
    script.src = scriptElementFromRef.src;

    Array.from(scriptElementFromRef.attributes).forEach((attr) => {
      script.setAttribute(attr.name, attr.value);
    });

    script.async = true;

    ref.current.removeChild(scriptElementFromRef);
    ref.current.appendChild(script);
  }
}

interface CheckoutAbortedEvent extends Event {
  detail?: {
    payload: {
      errorCode: "AGE_RESTRICTED_CART" | "DENIED" | "UNKNOWN_ERROR";
      reason: "ERROR" | "USER_CANCELLED";
    };
  };
}

const WALLEY_VALIDATION_KEY = "walleyCheckoutOrderValidationFailed";
const WALLEY_ABORTED_KEY = "walleyCheckoutPaymentAborted";
/**
 * Listen for events from Walley
 */
export function useWalleyCheckoutEvents(key?: string) {
  const { data: cart, refetch } = useCheckout();
  const { checkoutDenied } = useEvents();
  const queryClient = useQueryClient();

  const handleValidation = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["cart"] });
    void refetch();
  }, [queryClient, refetch]);

  const handleAborted = useCallback(
    (e: CheckoutAbortedEvent) => {
      if (e.detail?.payload.errorCode === "DENIED") {
        checkoutDenied(cart);
      }
    },
    [cart, checkoutDenied],
  );

  useEffect(() => {
    if (!key) return;
    const controller = new AbortController();
    document.addEventListener(WALLEY_ABORTED_KEY, handleAborted, controller);
    document.addEventListener(
      WALLEY_VALIDATION_KEY,
      handleValidation,
      controller,
    );
    return () => controller.abort();
  }, [handleAborted, handleValidation, key]);
}
